// ---------------------------------------------------------------------------
// The Stock tab row's Material pick list, and the per-element-type grouping
// every other consumer works on, as pure functions.
//
// One option per material name on the floor — the element types it appears as
// ("Slab · Wall") are a hint under the name, never a second pick: the site
// holds at most one stock per material (spec §5.2). The types themselves are
// picked further down the row, element by element.
//
// i18n via the passed-in `t` — unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { TFunction } from 'i18next';
import type { ITakeoffMaterial, ITakeoffMeasure } from '@interfaces/ICadTakeoff';
import type { IPickComboOption } from '@components/ui/PickCombo/PickCombo';
import { TAKEOFF_UNIT_KEYS } from './claimElements';
import { cadRowsFor, materialNamesForFloor } from './takeoffByFloor';

/** The Material list's "Not in the CAD…" opt-out row. Option values are
 *  otherwise material names, so the sentinel is one no CAD could carry. */
export const FREE_TEXT_OPTION = '\u0000free-text';

export function materialOptions(materials: ITakeoffMaterial[], t: TFunction): IPickComboOption[] {
  return [
    ...materialNamesForFloor(materials).map((name) => ({
      value: name,
      label: name,
      hint: [...new Set(cadRowsFor(materials, name).map((row) => row.kind))].join(' · '),
    })),
    { value: FREE_TEXT_OPTION, label: t('scheduler.panel.stock.notInCad'), separatorBefore: true, pinned: true },
  ];
}

/** Adds one storey's measure into the running group total — both the summed
 *  quantity and how many elements reported it, so the group's coverage figure
 *  stays true across every storey. */
function addMeasure(into: ITakeoffMeasure | undefined, add: ITakeoffMeasure): ITakeoffMeasure {
  return into === undefined
    ? { ...add }
    : { total: into.total + add.total, elements: into.elements + add.elements };
}

/**
 * The material's rows collapsed to ONE row per element type, `elementCount`,
 * every measure and the element lists summed. The take-off keys its rows by
 * storey as well, so a material spanning several floors — or the whole
 * building, when the task has no location — arrives as the same type many
 * times over; every consumer (options hint, ceiling, the element list, the
 * row itself) works on the grouped form so a type is counted, capped and
 * listed exactly once.
 */
export function groupRowsByKind(rows: ITakeoffMaterial[]): ITakeoffMaterial[] {
  const byKind = new Map<string, ITakeoffMaterial>();
  for (const row of rows) {
    const seen = byKind.get(row.kind);
    if (seen === undefined) {
      byKind.set(row.kind, { ...row, measures: { ...row.measures }, elements: [...row.elements] });
      continue;
    }
    seen.elementCount += row.elementCount;
    seen.elements.push(...row.elements);
    for (const key of TAKEOFF_UNIT_KEYS) {
      const add = row.measures[key];
      if (add !== undefined) seen.measures[key] = addMeasure(seen.measures[key], add);
    }
  }
  return [...byKind.values()];
}
